import type { BaseUnit, PackSize } from './types.js';
import { convert, round2 } from './units.js';

export interface PackChoice {
  /** Shelf instruction, e.g. "750g pack + 500g pack". */
  label: string;
  /** What you actually end up with, in base units. */
  totalQty: number;
  packs: { label: string; count: number }[];
}

/**
 * Upper bound on packs of one ingredient. A search guard, not a business rule.
 *
 * Two measured consequences, both unreachable from real seed data. The search
 * can return a slightly suboptimal answer when the true optimum needs more than
 * 50 packs — 33 cases in a 97,693-case sweep, all around 19kg, losing 10-50g.
 * And beyond `MAX_PACKS x largest pack` the fallback below takes over. A weekly
 * family shop never asks for 25kg of one ingredient.
 */
const MAX_PACKS = 50;

interface SizeInBase {
  label: string;
  base: number;
}

/**
 * Choose the pack combination that covers `required` with the least waste,
 * tie-broken by fewest packs.
 *
 * Deliberately exhaustive rather than greedy: needing 1200g from 1kg/750g/500g
 * packs, greedy largest-first gives 1500g, while the optimum is 750g+500g=1250g.
 * Pack lists have two to four entries, so exact search is cheap.
 */
export function roundToPacks(
  required: number,
  packs: PackSize[],
  baseUnit: BaseUnit,
): PackChoice | null {
  if (!Number.isFinite(required) || required <= 0) {
    // NaN <= 0 is false, so without the finite check NaN slips through, maxK
    // becomes NaN, the loop never runs and the fallback emits a shelf label
    // with totalQty: NaN — a line telling someone to buy an unknown amount.
    throw new Error(`Required quantity must be a positive, finite number, got ${required}`);
  }
  if (packs.length === 0) return null;

  const sizes: SizeInBase[] = packs
    .map((p) => ({ label: p.label, base: convert(p.packQty, p.unit, baseUnit) }))
    .filter((s) => s.base > 0)
    .sort((a, b) => b.base - a.base);

  if (sizes.length === 0) return null;

  // A holder object, not a bare `let`: TypeScript narrows a closure-assigned
  // local to `never` after the call, which a plain variable would trip over.
  const state: { best: { counts: number[]; total: number; packCount: number } | null } = {
    best: null,
  };

  const counts = new Array<number>(sizes.length).fill(0);

  const search = (index: number, total: number, packCount: number): void => {
    const best = state.best;
    if (total >= required) {
      if (best === null || total < best.total || (total === best.total && packCount < best.packCount)) {
        state.best = { counts: [...counts], total, packCount };
      }
      return; // adding more packs can only increase waste
    }
    if (index >= sizes.length || packCount >= MAX_PACKS) return;
    // No incumbent-based prune here: it would be dead code. Execution only
    // reaches this line when total < required, and any incumbent satisfies
    // best.total >= required, so `total >= best.total` can never hold. The
    // coverage return above is the only prune the search needs.

    const size = sizes[index]!;
    const maxK = Math.min(MAX_PACKS - packCount, Math.ceil((required - total) / size.base));
    for (let k = maxK; k >= 0; k--) {
      counts[index] = k;
      search(index + 1, round2(total + k * size.base), packCount + k);
    }
    counts[index] = 0;
  };

  search(0, 0, 0);

  const best = state.best;

  // Fallback: `required` exceeds MAX_PACKS x the largest pack. Cover it with
  // the biggest pack rather than returning nothing. This path deliberately
  // drops BOTH the minimise-waste and fewest-packs criteria; it guarantees
  // only that the total is >= required.
  if (best === null) {
    const largest = sizes[0]!;
    const count = Math.ceil(required / largest.base);
    return {
      label: formatLabel([{ label: largest.label, count }]),
      totalQty: round2(count * largest.base),
      packs: [{ label: largest.label, count }],
    };
  }

  const selected = sizes
    .map((size, i) => ({ label: size.label, count: best.counts[i] ?? 0 }))
    .filter((entry) => entry.count > 0);

  return { label: formatLabel(selected), totalQty: best.total, packs: selected };
}

function formatLabel(selected: { label: string; count: number }[]): string {
  return selected
    .map((entry) => (entry.count > 1 ? `${entry.count} × ${entry.label}` : entry.label))
    .join(' + ');
}
